package com.appsyde.shared.presentation.style.style

import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.token.AppStylesTokens
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.StyleSheet
 import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.flexWrap
 import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontStyle
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
 import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.letterSpacing
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginLeft
import org.jetbrains.compose.web.css.marginRight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.opacity
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.css.width

object InsightStyles : StyleSheet() {

    private const val TEXT = "rgba(20, 22, 40, 0.92)"
    private const val MUTED = "rgba(20, 22, 40, 0.6)"
    private const val DIVIDER = "rgba(20, 22, 40, 0.08)"

    // -------------------- Root safety --------------------
    val sidebarRoot by style { minWidth(0.px) }

    val divider by style {
        height(1.px)
        marginTop(10.px)
        marginBottom(10.px)
        property(
            "background",
            "linear-gradient(to right, rgba(15,23,42,0.00), rgba(15,23,42,0.10), rgba(15,23,42,0.00))"
        )
    }

    val summaryWrap by style {
        paddingTop(6.px)
        paddingBottom(10.px)
        property("max-width", "340px") // limite lecture
    }

    val summaryWrapProcessing by style {
        borderRadius(10.px)
        paddingLeft(12.px)
        paddingRight(12.px)
        property(
            "background",
            "linear-gradient(120deg, rgba(102,96,230,0.06), rgba(96,188,217,0.08), rgba(102,96,230,0.06))"
        )
        property("transition", "background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease")
    }

    val summaryWrapProcessingPulse by style {
        property("background", "linear-gradient(120deg, rgba(102,96,230,0.10), rgba(96,188,217,0.12))")
        property("box-shadow", "inset 0 0 0 1px rgba(102,96,230,0.10)")
    }

    /** Même force que Résumé, sans marge (pour Ajustements, Prêt, etc.). */
    val sectionLabel by style {
        fontSize(12.px)
        fontWeight(600)
        color(rgba(20, 22, 40, 0.65))
    }

    val summaryLabel by style {
        fontSize(12.px)
        fontWeight(600)
        color(rgba(20, 22, 40, 0.65))
        marginBottom(6.px)
    }

    val summaryText by style {
        fontSize(14.px)
        lineHeight(21.px)
        fontWeight(400)
        color(Color(TEXT))
        property("hyphens", "none")
        property("white-space", "normal")
    }

    val summaryTextEmpty by style {
        fontSize(13.px)
        fontWeight(500)
        color(Color(MUTED))
        property("font-style", "italic")
    }

    val copilotDivider by style {
        height(1.px)
        property("background", DIVIDER)
        marginTop(12.px)
        marginBottom(12.px)
    }

    /** Conteneur Copilote : pas de card, pas d’ombre, fond = colonne, padding vertical généreux */
    val copilotRoot by style {
        paddingTop(8.px)
        paddingBottom(24.px)
        minWidth(0.px)
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
    }

    /** Bloc Prochaine action : fond ~4% violet, bordure gauche 2px, coins légers, pas d’ombre */
    val copilotDirectionBlock by style {
        backgroundColor(Color("#ffffff"))
        property("border-left", "2px solid rgba(102,96,230,0.35)")
        borderRadius(8.px)
        padding(6.px, 16.px)
        property("box-sizing", "border-box")
        marginTop(10.px)
        marginBottom(10.px)
    }

    /** "Prochaine étape" : pas caps, pas espacé, petite taille, semi-bold, gris moyen */
    val copilotStepLabel by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color("rgba(20, 22, 40, 0.68)"))
        marginBottom(4.px)
    }

    /** Titre de résolution au-dessus du bloc Prochaine action (ex. Choix de solution) — même couleur que Contexte. */
    val copilotResolutionTitle by style {
        fontSize(13.px)
        fontWeight(650)
        color(Color("rgba(102,96,230,0.85)"))
        marginBottom(6.px)
    }

    /** Action principale : un peu plus grande (16px), bold, noir profond */
    val copilotStepAction by style {
        fontSize(16.px)
        fontWeight(700)
        property("letter-spacing", "-0.014em")
        property("line-height", "1.32")
        color(Color(TEXT))
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
    }

    /** Why : plus lisible (12px, contraste légèrement renforcé) */
    val copilotStepWhy by style {
        marginTop(6.px)
        paddingTop(0.px)
        fontSize(12.px)
        property("line-height", "1.35")
        color(Color("rgba(20, 22, 40, 0.55)"))
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
    }

    /** Séparateur horizontal fin entre sections (Prochaine action / À collecter / À confirmer) */
    /** Séparateur entre sections : contraste réduit, pas full width. */
    val copilotSectionDivider by style {
        height(1.px)
        width(92.percent)
        property("max-width", "100%")
        property("margin-left", "auto")
        property("margin-right", "auto")
        backgroundColor(Color("rgba(15,23,42,0.055)"))
        marginTop(20.px)
        marginBottom(16.px)
    }
    /** Ligne sous les sections : très discrète, pas full width (évite effet barre). */
    val collapseDivider by style {
        height(1.px)
        width(92.percent)
        property("max-width", "100%")
        property("margin-left", "auto")
        property("margin-right", "auto")
        backgroundColor(Color("rgba(15,23,42,0.04)"))
        marginTop(8.px)
        marginBottom(16.px)
    }

    /** Titre "À collecter" : 12px, poids 500 pour s’intégrer au flux. */
    val copilotSectionTitleCollect by style {
        fontSize(12.px)
        fontWeight(500)
        color(Color("rgba(20, 22, 40, 0.55)"))
        marginBottom(12.px)
    }

    /** Même style que À collecter, sans marge (titres Informations, Preuves, Documents). */
    val copilotSectionTitleCollectInline by style {
        fontSize(12.px)
        fontWeight(500)
        color(Color("rgba(20, 22, 40, 0.65)"))
    }

    /** Chevron des sections repliables (plus petit, même famille que le titre). */
    val collapsibleSectionChevron by style {
        fontSize(10.px)
        color(Color("rgba(102,96,230,0.65)"))
    }

    /** Compteur (N) dans le titre : plus saturé pour que la criticité soit visible d’un coup d’œil */
    val copilotSectionCount by style {
        color(Color("rgba(88, 80, 200, 0.98)"))
        fontWeight(700)
    }

    /** Titre "À confirmer" : 14px, poids normal, gris neutre (priorité secondaire) */
    val copilotSectionTitleConfirm by style {
        fontSize(12.px)
        fontWeight(500)
        color(Color("rgba(20, 22, 40, 0.55)"))
        marginBottom(12.px)
    }

    /** Titre de section générique (legacy) */
    val copilotSectionTitle by style {
        fontSize(14.px)
        fontWeight(700)
        color(Color(MUTED))
        marginBottom(10.px)
    }

    /** Item liste Copilote : fond toujours visible (léger), au hover un peu plus marqué */
    val copilotListItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
        minWidth(0.px)
        width(100.percent)
        padding(6.px, 12.px)
        borderRadius(6.px)
        property("box-sizing", "border-box")
        backgroundColor(Color("rgba(102,96,230,0.03)"))
        property("transition", "background-color 140ms ease")
        self + hover style {
            backgroundColor(Color("rgba(102,96,230,0.06)"))
        }
    }

    /** Ligne cliquable (ex. À collecter) : curseur pointer */
    val copilotListItemClickable by style {
        property("cursor", "pointer")
    }

    /** Ligne « À compléter » (missing) : fond et texte orange, hover plus intense */
    val copilotListItemMissing by style {
        backgroundColor(Color("rgba(180,83,9,0.06)"))
        property("transition", "background-color 160ms ease")
        self + hover style {
            backgroundColor(Color("rgba(180,83,9,0.12)"))
        }
    }

    val copilotListItemEditing by style {
        backgroundColor(Color("transparent"))
    }

    /** Label dans une ligne à compléter (missing) : texte orange */
    val copilotListItemLabelMissing by style {
        fontSize(13.px)
        fontWeight(550)
        color(Color("rgba(154,70,8,0.9)"))
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
    }

    val copilotListItemLabel by style {
        fontSize(13.px)
        fontWeight(550)
        color(Color(AppStylesTokens.INK_SOFT))
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
    }

    /** Bouton [+] ou Valider : discret, apparaît au hover (géré par le DOM) */
    val copilotListAction by style {
        flexShrink(0)
        padding(6.px, 12.px)
        borderRadius(6.px)
        fontSize(12.px)
        fontWeight(600)
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.12)"))
        backgroundColor(Color("rgba(255,255,255,0.9)"))
        color(Color("rgba(15,23,42,0.78)"))
        property("cursor", "pointer")
        property("transition", "background-color 140ms ease, border-color 140ms ease, opacity 140ms ease")
        self + hover style {
            backgroundColor(Color("rgba(255,255,255,1)"))
            property("border-color", "rgba(15,23,42,0.18)")
        }
    }

    /** Bouton [+] à collecter : icône faint (30%) toujours visible, actif au hover */
    val copilotListActionFaint by style {
        flexShrink(0)
        padding(6.px, 12.px)
        borderRadius(6.px)
        fontSize(12.px)
        fontWeight(600)
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.10)"))
        backgroundColor(Color("rgba(255,255,255,0.6)"))
        color(Color("rgba(15,23,42,0.5)"))
        property("cursor", "pointer")
        opacity(0.3)
        property("transition", "opacity 140ms ease, background-color 140ms ease, border-color 140ms ease")
        self + hover style {
            opacity(1)
            backgroundColor(Color("rgba(255,255,255,0.9)"))
            property("border-color", "rgba(15,23,42,0.18)")
            color(Color("rgba(15,23,42,0.78)"))
        }
    }

    /** Bouton [+] dans une ligne À compléter (missing) : faint par défaut, intense au hover */
    val copilotListActionFaintMissing by style {
        flexShrink(0)
        padding(6.px, 12.px)
        borderRadius(6.px)
        fontSize(12.px)
        fontWeight(600)
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.18)"))
        backgroundColor(Color("rgba(255,255,255,0.5)"))
        color(Color("rgba(154,70,8,0.55)"))
        property("cursor", "pointer")
        opacity(0.7)
        property("transition", "opacity 140ms ease, background-color 140ms ease, border-color 140ms ease, color 140ms ease")
        self + hover style {
            opacity(1)
            backgroundColor(Color("rgba(180,83,9,0.1)"))
            property("border-color", "rgba(180,83,9,0.35)")
            color(Color("rgba(154,70,8,0.95)"))
        }
    }

    /** Bouton Valider : ghost discret, icône ✓ au hover (texte "Valider" toujours visible) */
    val copilotListActionValiderGhost by style {
        flexShrink(0)
        padding(6.px, 12.px)
        borderRadius(6.px)
        fontSize(12.px)
        fontWeight(600)
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.08)"))
        backgroundColor(Color.transparent)
        color(Color("rgba(15,23,42,0.5)"))
        property("cursor", "pointer")
        property("transition", "background-color 140ms ease, border-color 140ms ease, color 140ms ease")
        self + hover style {
            backgroundColor(Color("rgba(34,197,94,0.06)"))
            property("border-color", "rgba(34,197,94,0.22)")
            color(Color("rgba(21,128,61,0.88)"))
        }
    }

    /** Icône ✓ devant "Valider" (affichée au hover de la ligne) */
    val copilotListActionIcon by style {
        marginRight(4.px)
        fontSize(12.px)
        fontWeight(600)
    }

    val copilotListActionConfirm by style {
        property("border-color", "rgba(34,197,94,0.28)")
        backgroundColor(Color("rgba(34,197,94,0.08)"))
        color(Color("rgba(21,128,61,0.92)"))
        self + hover style {
            backgroundColor(Color("rgba(34,197,94,0.14)"))
            property("border-color", "rgba(34,197,94,0.38)")
        }
    }

    /** Séparation nette entre sections */
    val copilotBlockDivider by style {
        height(1.px)
        width(100.percent)
        backgroundColor(Color("rgba(15,23,42,0.08)"))
        marginTop(20.px)
        marginBottom(16.px)
    }

    val summaryInlineLabel by style {
        fontSize(11.px)
        fontWeight(700)
        color(Color(MUTED))
        letterSpacing(0.2.px)
        marginRight(6.px)
    }

    val panelCard by style {
        padding(20.px)
        paddingBottom(22.px)
        property("box-sizing", "border-box")
        minWidth(0.px)
        width(100.percent)
        overflow("hidden")

        borderRadius(18.px)
        backgroundColor(rgba(255, 255, 255, 0.82))
        border(1.px, LineStyle.Solid, rgba(15, 23, 42, 0.06))
        property("box-shadow", AppStylesTokens.SHADOW)
        property("backdrop-filter", "blur(10px)")
        property("-webkit-backdrop-filter", "blur(10px)")
    }

    val panelCardProcessing by style {
        border(1.px, LineStyle.Solid, rgba(102, 96, 230, 0.16))
        property("box-shadow", "0 10px 28px rgba(102,96,230,0.12)")
        property("transition", "box-shadow 180ms ease, border-color 180ms ease")
    }

    val panelCardProcessingPulse by style {
        border(1.px, LineStyle.Solid, rgba(102, 96, 230, 0.24))
        property("box-shadow", "0 12px 32px rgba(102,96,230,0.18)")
    }

    // -------------------- Header --------------------
    val panelHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        paddingBottom(10.px)
        marginBottom(14.px)
        minWidth(0.px)
    }

    /** Header cliquable pour carte Contexte collapsible */
    val panelHeaderCollapsible by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        paddingBottom(10.px)
        marginBottom(0.px)
        minWidth(0.px)
        property("cursor", "pointer")
        property("user-select", "none")
    }

    /** Mode replié : moins de padding sous le header pour équilibrer */
    val panelHeaderCollapsibleCollapsed by style {
        paddingBottom(0.px)
    }

    val panelHeaderChevron by style {
        fontSize(10.px)
        color(Color("rgba(15,23,42,0.45)"))
        marginLeft(8.px)
        property("transition", "transform 160ms ease")
    }

    /** Quand la carte Contexte est dépliée, marge sous le header */
    val panelHeaderCollapsibleExpanded by style {
        marginBottom(14.px)
    }

    /** Carte Contexte repliée : garder un padding bas confortable pour ne pas coller en bas */
    val contextCardCollapsed by style {
        paddingBottom(22.px)
    }

    /** Wrapper du corps Contexte pour animation smooth expand/collapse (grid pour hauteur réelle, pas max-height) */
    val contextBodyWrap by style {
        display(DisplayStyle.Grid)
        property("transition", "grid-template-rows 220ms ease")
    }

    val contextBodyCollapsed by style {
        property("grid-template-rows", "0fr")
    }

    val contextBodyExpanded by style {
        property("grid-template-rows", "1fr")
    }

    /** Enfant direct du grid : min-height 0 pour que 0fr/1fr fonctionne, overflow pour clip */
    val contextBodyInner by style {
        minHeight(0.px)
        overflow("hidden")
    }

    /** Groupe status pill + chevron à droite du header collapsible */
    val panelHeaderRight by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
    }

    val section by style {
        paddingTop(6.px)
        paddingBottom(6.px)
    }

    val sectionDivider by style {
        height(1.px)
        width(100.percent)
        backgroundColor(Color("rgba(15,23,42,0.06)"))
        marginTop(18.px)
        marginBottom(18.px)
    }

    // -------------------- Action suggested (hero minimal, style citation) --------------------
    val actionSurface by style {
        position(Position.Relative)
        backgroundColor(Color("rgba(102,96,230,0.04)"))
        property("border-left", "4px solid rgba(102,96,230,0.42)")
        borderRadius(10.px)
        padding(14.px, 16.px)
        property("padding-left", "18px")
        property("transition", "border-color 160ms ease")
        self + hover style {
            property("border-left-color", "rgba(102,96,230,0.55)")
        }
    }

    val panelTitleWrap by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
        minWidth(0.px)
        marginRight(14.px)
    }

    /** Kicker (Synthèse, Ticket) : label discret, sous le titre. */
    val panelKicker by style {
        fontSize(11.px)
        fontWeight(500)
        property("letter-spacing", "0.02em")
        color(Color("rgba(102,96,230,0.52)"))
    }

    /** Titre de section (Dossier, Contexte) : affirmé. */
    val panelTitle by style {
        fontSize(14.px)
        fontWeight(600)
        property("letter-spacing", "-0.012em")
        color(Color("rgba(15,23,42,0.85)"))
    }

    // -------------------- Status pill (Analysé, En attente, Preuve photo...) --------------------
    val statusPill by style {
        padding(6.px, 12.px)
        borderRadius(999.px)
        fontSize(12.px)
        fontWeight(600)
        property("letter-spacing", "-0.01em")
        flexShrink(0)
        backgroundColor(rgba(255, 255, 255, 0.82))
        border(1.px, LineStyle.Solid, rgba(15, 23, 42, 0.08))
        property("box-shadow", "0 1px 0 rgba(255,255,255,0.85)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
    }

    val statusDot by style {
        width(7.px)
        height(7.px)
        borderRadius(50.percent)
        backgroundColor(rgba(59, 70, 212, 0.55))
        flexShrink(0)
    }

    /** Dot "Analyse en cours" : Appsyde légèrement adouci + clignotement */
    val statusDotProcessing by style {
        backgroundColor(Color("rgba(102,96,230,0.88)"))
        property("box-shadow", "0 0 0 4px rgba(102,96,230,0.14)")
        property("animation", "${AppStylesAnimations.dotBlink.name} 1s ease-in-out infinite")
    }

    val statusDotProcessingPulse by style {
        opacity(0.78)
        property("transform", "scale(0.84)")
        property("box-shadow", "0 0 0 7px rgba(102,96,230,0.09)")
    }

    val statusPillOk by style {
        color(Color("rgba(15,23,42,0.72)"))
        backgroundColor(Color("rgba(15,23,42,0.04)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.12)"))
    }

    val statusPillProcessing by style {
        color(Color("rgba(59, 70, 212, 0.98)"))
        property(
            "background",
            "linear-gradient(120deg, rgba(102,96,230,0.12), rgba(96,188,217,0.14), rgba(102,96,230,0.12))"
        )
        border(1.px, LineStyle.Solid, Color("rgba(102,96,230,0.24)"))
        property("box-shadow", "0 3px 10px rgba(102,96,230,0.14)")
        property("transition", "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease")
    }

    val statusPillProcessingPulse by style {
        property("background", "linear-gradient(120deg, rgba(102,96,230,0.18), rgba(96,188,217,0.18))")
        border(1.px, LineStyle.Solid, Color("rgba(102,96,230,0.34)"))
        property("box-shadow", "0 5px 14px rgba(102,96,230,0.18)")
    }

    val statusPillMid by style {
        color(Color(AppStylesTokens.INK_SOFT))
        backgroundColor(Color("rgba(15,23,42,0.03)"))
        border(1.px, LineStyle.Solid, Color(AppStylesTokens.BORDER))
    }

    /** Point "Analysé" : état fin, couleur Appsyde légèrement adoucie */
    val statusDotOk by style {
        backgroundColor(Color("rgba(102,96,230,0.88)"))
        property("box-shadow", "0 0 0 2px rgba(102,96,230,0.22)")
    }

    val statusPillLow by style {
        color(Color("rgba(120,90,220,0.86)"))
        backgroundColor(Color("rgba(120,90,220,0.055)"))
        border(1.px, LineStyle.Solid, rgba(120, 90, 220, 0.16))
    }

    val statusPillWarn by style {
        color(Color(AppStylesTokens.STATUS_WAITING))
        backgroundColor(Color(AppStylesTokens.STATUS_WAITING_SOFT))
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.32)"))
    }

    // -------------------- Meta row --------------------
    val metaRow by style {
        marginTop(6.px)
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(6.px)
        alignItems(AlignItems.Center)
        color(Color(AppStylesTokens.INK_MUTED))
        fontSize(11.5.px)
        fontWeight(550)
        property("letter-spacing", "-0.004em")
        paddingTop(4.px)
        paddingBottom(4.px)
    }

    val metaLabel by style {
        color(Color(AppStylesTokens.INK_MUTED))
        fontWeight(500)
        fontSize(11.5.px)
    }

    val metaLabelLight by style {
        fontSize(14.px)
        color(Color("rgba(138,144,165,0.95)"))
        fontWeight(450)
    }

    val metaValue by style {
        color(Color(AppStylesTokens.INK_SOFT))
        fontWeight(600)
        fontSize(11.5.px)
    }

    val metaDot by style {
        width(6.px)
        height(6.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(102,96,230,0.30)"))
        property("box-shadow", "0 0 0 3px rgba(102,96,230,0.05)")
    }

    // -------------------- Score ring --------------------
    val scoreTextCol by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        minWidth(0.px)
        property("flex", "1 1 0")
    }

    val scoreTitle by style {
        fontSize(14.5.px)
        fontWeight(650)
        color(Color(AppStylesTokens.INK_SOFT))
        lineHeight("1.22")
        property("letter-spacing", "-0.012em")
        property("display", "-webkit-box")
        property("-webkit-box-orient", "vertical")
        property("-webkit-line-clamp", "2")
        property("overflow", "hidden")
    }

    val scoreRingFill by style {
        width(100.percent)
        height(100.percent)
        borderRadius(50.percent)
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.70)")
    }

    val scoreSubtitle by style {
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        lineHeight("1.35")
        property("display", "-webkit-box")
        property("-webkit-box-orient", "vertical")
        property("-webkit-line-clamp", "2")
        property("overflow", "hidden")
    }

    // -------------------- Action hero block --------------------
    val heroAction by style {
        marginTop(12.px)
        borderRadius(18.px)
        padding(14.px)
        property("box-sizing", "border-box")
        width(100.percent)
        minWidth(0.px)
        overflow("hidden")
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.92))")
        border(1.px, LineStyle.Solid, rgba(102, 96, 230, 0.12))
        property("backdrop-filter", "blur(10px)")
        property("-webkit-backdrop-filter", "blur(10px)")
        property("box-shadow", "0 10px 30px rgba(15,23,42,0.04), inset 0 1px 0 ${AppStylesTokens.HILITE}")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
    }

    val actionHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
    }

    val actionIcon by style {
        width(26.px)
        height(26.px)
        borderRadius(8.px)
        backgroundColor(Color("rgba(102,96,230,0.10)"))
        color(Color(AppStylesTokens.PURPLE))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(13.px)
        fontWeight(750)
        flexShrink(0)
    }

    val actionTitleCol by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
        minWidth(0.px)
    }

    val actionTitle by style {
        fontSize(14.px)
        fontWeight(700)
        property("letter-spacing", "-0.012em")
        color(Color(AppStylesTokens.INK_SOFT))
    }

    /** Petit label secondaire au-dessus de la phrase hero */
    val actionSub by style {
        fontSize(10.5.px)
        fontWeight(600)
        property("letter-spacing", "0.04em")
        color(Color("rgba(102,96,230,0.62)"))
        marginBottom(6.px)
    }

    val actionRow by style {
        display(DisplayStyle.Flex)
        gap(12.px)
        alignItems(AlignItems.Center)
        minWidth(0.px)
    }

    val actionBullet by style {
        width(8.px)
        height(8.px)
        borderRadius(50.percent)
        marginTop(7.px)
        backgroundColor(Color("rgba(102,96,230,0.34)"))
        property("box-shadow", "0 0 0 3px rgba(102,96,230,0.05)")
        flexShrink(0)
    }

    /** Phrase d’action en hero (focus unique du bloc) */
    val actionText by style {
        fontSize(16.px)
        fontWeight(700)
        property("line-height", "1.35")
        color(Color(AppStylesTokens.INK_SOFT))
        minWidth(0.px)
        property("flex", "1 1 0")
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
        property("word-break", "break-word")
    }

    val actionTextEmpty by style {
        fontSize(13.px)
        fontWeight(500)
        color(Color(AppStylesTokens.INK_MUTED))
        property("font-style", "italic")
    }

    /** Ligne "pourquoi" sous l'action recommandée (discret) */
    val actionWhy by style {
        marginTop(6.px)
        paddingLeft(0.px)
        fontSize(12.px)
        lineHeight("1.35")
        color(Color(MUTED))
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
    }

    /** Badge discret à côté d'une donnée extraite : Saisi / Validé (source agent) */
    val sourceBadge by style {
        marginLeft(6.px)
        paddingLeft(6.px)
        paddingRight(6.px)
        borderRadius(4.px)
        fontSize(10.px)
        fontWeight(500)
        color(Color("rgba(20,22,40,0.55)"))
        property("background", "rgba(102,96,230,0.06)")
        property("white-space", "nowrap")
    }

    // -------------------- Blocks: detected + missing --------------------
    val blockHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        marginBottom(8.px)
    }
    val blockIconNeutral by style {
        width(26.px)
        height(26.px)
        borderRadius(10.px)

        backgroundColor(Color("rgba(15,23,42,0.03)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))

        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)

        fontSize(13.px)
        fontWeight(750)
        flexShrink(0)

        property("transition", "transform 140ms ease, background-color 140ms ease, border-color 140ms ease")
    }
    val blockIcon by style {
        width(26.px)
        height(26.px)
        borderRadius(10.px)
        backgroundColor(Color("rgba(15,23,42,0.03)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(13.px)
        fontWeight(750)
        flexShrink(0)
        property("transition", "transform 140ms ease, background-color 140ms ease, border-color 140ms ease")
    }

    // ✅ UPDATED: warm amber (pas rouge)
    val blockIconWarn by style {
        width(26.px)
        height(26.px)
        borderRadius(10.px)
        backgroundColor(Color("rgba(245, 158, 11, 0.14)"))
        border(1.px, LineStyle.Solid, Color("rgba(245, 158, 11, 0.28)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(13.px)
        fontWeight(750)
        flexShrink(0)
        property("transition", "transform 140ms ease, background-color 140ms ease, border-color 140ms ease")
    }

    val blockTitle by style {
        fontSize(12.8.px)
        fontWeight(550)
        property("letter-spacing", "-0.010em")
        color(Color(AppStylesTokens.INK_SOFT))
        minWidth(0.px)
    }

    // ✅ UPDATED: amber-700-ish (pas rouge)
    val blockTitleWarn by style {
        fontSize(13.px)
        fontWeight(700)
        property("letter-spacing", "-0.008em")
        color(Color("rgba(180, 83, 9, 0.95)"))
        minWidth(0.px)
    }

    // -------------------- Données utiles (premium style) --------------------
    val dataUsefulHeader by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        minWidth(0.px)
        marginBottom(12.px)
    }

    val dataUsefulTitle by style {
        fontSize(11.5.px)
        fontWeight(700)
        property("letter-spacing", "0.04em")
        color(Color("rgba(102,96,230,0.75)"))
        minWidth(0.px)
        marginBottom(8.px)
    }

    val dataUsefulDivider by style {
        height(1.px)
        width(100.percent)
        backgroundColor(Color("rgba(102,96,230,0.12)"))
        marginBottom(0.px)
        property(
            "background",
            "linear-gradient(to right, rgba(102,96,230,0.20), rgba(102,96,230,0.08), transparent)"
        )
    }

    val infoList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        minWidth(0.px)
    }

    val infoRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        minWidth(0.px)
        padding(10.px, 8.px)
        borderRadius(8.px)
        backgroundColor(Color("rgba(0,0,0,0)"))
        border(0.px, LineStyle.Solid, Color("transparent"))
        property("box-shadow", "none")
       // property("border-bottom", "1px solid rgba(15,23,42,0.06)")
    }

    val infoRowLast by style { property("border-bottom", "none") }

    /** Icône devant chaque donnée extraite (réduite pour rester discrète). */
    val infoIcon by style {
        width(18.px)
        height(18.px)
        borderRadius(6.px)
        fontSize(10.px)
        backgroundColor(Color("rgba(15,23,42,0.03)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontWeight(750)
        flexShrink(0)
        property("align-self", "center")
        property("line-height", "1")
        property("flex-shrink", "0")
    }

    val infoLabel by style {
        fontSize(12.2.px)
        fontWeight(550)
        color(Color("rgba(31, 42, 68, 0.52)"))
        property("flex", "0 0 auto")
        property("max-width", "130px")
        property("white-space", "normal")
        property("overflow-wrap", "break-word")
        property("word-break", "break-word")
        property("line-height", "1.35")
        property("align-self", "center")
    }

    val infoLabelShort by style {
        property("flex", "0 0 auto")
        property("min-width", "fit-content")
        property("white-space", "nowrap")
    }

    val infoLabelTwoLines by style {
        property("flex", "0 0 auto")
        property("min-width", "0")
        property("max-width", "none")
        property("white-space", "normal")
        property("overflow-wrap", "break-word")
        property("word-break", "break-word")
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        overflow("hidden")
    }

    val infoValue by style {
        property("font-size", "13.5px")
        style { property("font-variant-numeric", "tabular-nums") }
        fontWeight(600)
        color(Color("rgba(31, 42, 68, 0.82)"))
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "normal")
        property("overflow-wrap", "break-word")
        property("word-break", "break-word")
        property("line-height", "1.35")
        property("align-self", "center")
    }

    val infoRowWrap by style {
        property("flex-wrap", "wrap")
        property("row-gap", "6px")
        property("align-items", "center")
    }

    val infoRowWrapSingle by style {
        property("flex-wrap", "nowrap")
        property("align-items", "center")
    }

    /** Bloc valeur + badge + bouton copier aligné à droite (singleLine). */
    val infoRowRightBlock by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        property("margin-left", "auto")
        property("min-width", "0")
    }

    val infoLabelEllipsis by style {
        property("min-width", "0")
        property("flex", "1 1 0")
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val infoValueSingleLine by style {
        property("min-width", "0")
        property("flex", "1 1 0")
        property("flex-basis", "auto")
        property("white-space", "normal")
        property("overflow-wrap", "break-word")
        property("word-break", "break-word")
        property("margin-left", "0px")
    }

    val infoValueMultiLine by style {
        property("min-width", "0")
        property("flex", "1 1 0")
        property("flex-basis", "100%")
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
        property("word-break", "break-word")
    }

    // -------------------- Legacy surfaces (kept) --------------------
    val missingSurface by style {
        borderRadius(14.px)
        padding(12.px, 12.px)
        backgroundColor(Color("rgba(255,250,240,0.98)"))
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.18)"))
        property("box-shadow", "0 2px 12px rgba(180,83,9,0.05)")
    }

    val detectedSurface by style {
        borderRadius(16.px)
        padding(10.px, 8.px)
        paddingBottom(14.px)
        backgroundColor(Color("rgba(255,255,255,0.62)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.75)")
    }

    val missingRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        minWidth(0.px)
        justifyContent(JustifyContent.SpaceBetween)
        width(100.percent)

        padding(7.px, 14.px)
        borderRadius(999.px)

        backgroundColor(Color("rgba(255,255,252,0.98)"))
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.18)"))
        property("box-shadow", "0 1px 2px rgba(15,23,42,0.04)")
        property("transition", "background-color 160ms ease, border-color 160ms ease")

        marginBottom(8.px)
        fontSize(12.px)
        fontWeight(600)

        self + hover style {
            backgroundColor(Color("rgba(255,252,248,0.98)"))
            property("border-color", "rgba(180,83,9,0.28)")
        }
        self + lastChild style { marginBottom(0.px) }
    }

    val missingIcon by style {
        width(22.px)
        height(22.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(180,83,9,0.16)"))
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.28)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(11.px)
        fontWeight(700)
        flexShrink(0)
    }

    val pointItemContent by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        minWidth(0.px)
        property("flex", "1 1 0")
    }

    val missingText by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color(AppStylesTokens.STATUS_WAITING))
        minWidth(0.px)
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
        property("word-break", "break-word")
    }

    // -------------------- Points à traiter (legacy) --------------------
    val pointsToProcessSection by style {
        paddingTop(6.px)
        paddingBottom(6.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
    }

    val pointsUnifiedSurface by style {
        borderRadius(14.px)
        padding(12.px, 12.px)
        backgroundColor(Color("rgba(255,250,240,0.98)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.08)"))
        property("box-shadow", "0 2px 8px rgba(15,23,42,0.04)")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val confirmIcon by style {
        width(22.px)
        height(22.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(245, 158, 11, 0.14)"))
        border(1.px, LineStyle.Solid, Color("rgba(245, 158, 11, 0.22)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(11.px)
        fontWeight(700)
        flexShrink(0)
    }

    val confirmText by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color("rgba(154, 70, 8, 0.80)"))
        minWidth(0.px)
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
        property("word-break", "break-word")
    }

    // -------------------- Items interactifs (legacy) --------------------
    val pointItemRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        minWidth(0.px)
        padding(8.px, 12.px)
        borderRadius(999.px)
        property("transition", "all 160ms ease")
        justifyContent(JustifyContent.SpaceBetween)
        width(100.percent)
    }

    val pointItemRowEdit by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        minWidth(0.px)
        padding(10.px, 12.px)
        borderRadius(12.px)
        property("transition", "all 160ms ease")
        width(100.percent)
    }

    // (kept as-is, but note: these are now visually replaced by the "simple" system below)
    val pointItemMissing by style {
        backgroundColor(Color("rgba(254, 242, 242, 0.95)"))
        border(1.px, LineStyle.Solid, Color("rgba(185, 28, 28, 0.20)"))
        property("box-shadow", "0 1px 3px rgba(185, 28, 28, 0.08)")
        self + hover style {
            backgroundColor(Color("rgba(254, 242, 242, 1.0)"))
            property("border-color", "rgba(185, 28, 28, 0.30)")
            property("box-shadow", "0 2px 6px rgba(185, 28, 28, 0.12)")
        }
    }

    val pointItemConfirm by style {
        backgroundColor(Color("rgba(255, 251, 235, 0.95)"))
        border(1.px, LineStyle.Solid, Color("rgba(245, 158, 11, 0.18)"))
        property("box-shadow", "0 1px 3px rgba(245, 158, 11, 0.06)")
        self + hover style {
            backgroundColor(Color("rgba(255, 251, 235, 1.0)"))
            property("border-color", "rgba(245, 158, 11, 0.25)")
            property("box-shadow", "0 2px 6px rgba(245, 158, 11, 0.10)")
        }
    }

    val pointItemAdded by style {
        backgroundColor(Color("rgba(236, 253, 245, 0.95)"))
        border(1.px, LineStyle.Solid, Color("rgba(22, 163, 74, 0.20)"))
        property("box-shadow", "0 1px 3px rgba(22, 163, 74, 0.08)")
        opacity(0.85)
    }

    val pointItemConfirmed by style {
        backgroundColor(Color("rgba(236, 253, 245, 0.95)"))
        border(1.px, LineStyle.Solid, Color("rgba(22, 163, 74, 0.20)"))
        property("box-shadow", "0 1px 3px rgba(22, 163, 74, 0.08)")
        opacity(0.85)
    }

    val pointItemAction by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
        padding(4.px, 10.px)
        borderRadius(999.px)
        fontSize(11.px)
        fontWeight(600)
        backgroundColor(Color("rgba(102,96,230,0.10)"))
        border(1.px, LineStyle.Solid, Color("rgba(102,96,230,0.20)"))
        color(Color("rgba(102,96,230,0.90)"))
        property("cursor", "pointer")
        property("transition", "all 120ms ease")
        flexShrink(0)
        property("box-shadow", "0 1px 2px rgba(102,96,230,0.08)")
        self + hover style {
            backgroundColor(Color("rgba(102,96,230,0.15)"))
            property("border-color", "rgba(102,96,230,0.30)")
            property("transform", "translateY(-1px)")
            property("box-shadow", "0 2px 4px rgba(102,96,230,0.12)")
        }
        self + active style { property("transform", "translateY(0)") }
    }

    val pointItemActionIcon by style {
        fontSize(12.px)
        fontWeight(700)
        lineHeight("1")
    }

    val pointItemStatus by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
        fontSize(11.px)
        fontWeight(600)
        color(Color("rgba(22, 163, 74, 0.85)"))
        flexShrink(0)
    }

    val pointItemStatusIcon by style {
        fontSize(12.px)
        fontWeight(700)
        lineHeight("1")
    }

    // =========================================================================
    // ✅ NEW DESIGN: Missing / ToConfirm — simple premium lists
    // =========================================================================

    // Counters
    val pointCounter by style {
        fontSize(11.5.px)
        fontWeight(650)
        color(Color(AppStylesTokens.INK_MUTED))
        opacity(0.70)
        marginLeft(4.px)
    }

    val pointCounterWarn by style {
        fontSize(11.5.px)
        fontWeight(700)
        color(Color("rgba(180, 83, 9, 0.78)"))
        opacity(0.92)
        marginLeft(4.px)
    }

    /** Mini-pill pour le compteur à côté du titre de bloc (ex. "Informations manquantes" + pill "1") */
    val blockCountPill by style {
        display(DisplayStyle.LegacyInlineFlex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(2.px, 8.px)
        borderRadius(999.px)
        fontSize(11.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_MUTED))
        backgroundColor(Color("rgba(15,23,42,0.06)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.08)"))
        marginLeft(8.px)
        property("line-height", "1")
    }

    val blockCountPillWarn by style {
        color(Color("rgba(180, 83, 9, 0.92)"))
        backgroundColor(Color("rgba(245, 158, 11, 0.12)"))
        border(1.px, LineStyle.Solid, Color("rgba(245, 158, 11, 0.22)"))
    }

    // List container — constrains width so items don't overflow
    val pointsListSimple by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        minWidth(0.px)
        width(100.percent)
    }

    // Item row — content stays inside border, no overlap
    val pointItemSimple by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        width(100.percent)
        padding(12.px, 12.px)
        borderRadius(12.px)
        property("box-sizing", "border-box")

        backgroundColor(Color("rgba(255,255,255,0.88)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.09)"))

        property("transition", "background-color 140ms ease, border-color 140ms ease, transform 140ms ease")
        property("cursor", "pointer")

        self + hover style {
            backgroundColor(Color("rgba(255,255,255,0.96)"))
            property("border-color", "rgba(15,23,42,0.12)")
            property("transform", "translateY(-1px)")
        }
    }

    // En mode édition : pas de bordure ni padding → plus de place pour le champ
    val pointItemSimpleEditing by style {
        padding(0.px)
        border(0.px, LineStyle.Solid, Color("transparent"))
        backgroundColor(Color("rgba(0,0,0,0)"))
        property("box-shadow", "none")
    }

    // Edit row: icon + input + icon buttons — prevents overflow
    val pointItemEditRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        minWidth(0.px)
        property("flex", "1 1 0")
        width(100.percent)
    }

    // Icons base
    private val pointIconBase by style {
        width(22.px)
        height(22.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(12.5.px)
        fontWeight(800)
        flexShrink(0)
        property("line-height", "1")
        property("transition", "background-color 140ms ease, border-color 140ms ease, color 140ms ease")
    }

    // ✅ Missing icon = warm / amber (not red)
    val pointIconMissing by style {
        width(22.px)
        height(22.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(12.5.px)
        fontWeight(800)
        flexShrink(0)
        property("line-height", "1")
        property("transition", "background-color 140ms ease, border-color 140ms ease, color 140ms ease")
        backgroundColor(Color("rgba(245, 158, 11, 0.14)"))
        border(1.px, LineStyle.Solid, Color("rgba(245, 158, 11, 0.24)"))
        color(Color("rgba(180, 83, 9, 0.95)"))
    }

    // ✅ Confirm icon = neutral
    val pointIconConfirm by style {
        width(22.px)
        height(22.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(12.5.px)
        fontWeight(800)
        flexShrink(0)
        property("line-height", "1")
        property("transition", "background-color 140ms ease, border-color 140ms ease, color 140ms ease")
        backgroundColor(Color("rgba(99, 102, 241, 0.10)"))
        border(1.px, LineStyle.Solid, Color("rgba(99, 102, 241, 0.18)"))
        color(Color("rgba(55, 65, 81, 0.88)"))
    }

    // ✅ Validated icon
    val pointIconValidated by style {
        width(22.px)
        height(22.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        fontSize(12.5.px)
        fontWeight(800)
        flexShrink(0)
        property("line-height", "1")
        property("transition", "background-color 140ms ease, border-color 140ms ease, color 140ms ease")
        backgroundColor(Color("rgba(34, 197, 94, 0.12)"))
        border(1.px, LineStyle.Solid, Color("rgba(34, 197, 94, 0.22)"))
        color(Color("rgba(21, 128, 61, 0.95)"))
        marginRight(6.px)
    }

    // Labels
    val pointLabel by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_SOFT))
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    // ✅ Missing label = warm amber (not red)
    val pointLabelMissing by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color("rgba(180, 83, 9, 0.95)"))
        property("letter-spacing", "-0.008em")
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    // ✅ Confirm label = neutral (not orange)
    val pointLabelConfirm by style {
        fontSize(13.px)
        fontWeight(550)
        color(Color("rgba(15,23,42,0.78)"))
        property("letter-spacing", "-0.008em")
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val pointLabelConfirmed by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color("rgba(15,23,42,0.78)"))
        property("letter-spacing", "-0.008em")
        property("flex", "1 1 0")
        minWidth(0.px)
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val pointStatusText by style {
        marginLeft(8.px)
        fontSize(12.5.px)
        fontWeight(650)
        color(Color("rgba(21, 128, 61, 0.85)"))
        fontStyle("Italic")
    }

    val pointValue by style {
        marginLeft(6.px)
        fontWeight(650)
        color(Color("rgba(15,23,42,0.82)"))
    }

    // Inline input — takes full width when leading icon hidden in edit mode
    val pointInputInline by style {
        property("flex", "1 1 0")
        minWidth(0.px)
        height(30.px)
        paddingLeft(10.px)
        paddingRight(10.px)
        borderRadius(8.px)
        fontSize(13.px)
        fontWeight(500)
        lineHeight(1.35.px)

        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.12)"))
        backgroundColor(Color("rgba(255,255,255,0.95)"))
        color(Color("rgba(15,23,42,0.9)"))

        property("outline", "none")
        property("transition", "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease")

        self + focus style {
            property("border-color", "rgba(99,102,241,0.22)")
            property("box-shadow", "0 0 0 1px rgba(99,102,241,0.08)")
            backgroundColor(Color("rgba(255,255,255,1)"))
        }
    }

    // Inline actions (base)
    val pointActionInline by style {
        height(32.px)
        paddingLeft(10.px)
        paddingRight(10.px)
        borderRadius(10.px)

        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.10)"))
        backgroundColor(Color("rgba(255,255,255,0.85)"))
        color(Color("rgba(15,23,42,0.78)"))

        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexShrink(0)

        fontSize(12.5.px)
        fontWeight(650)

        property("cursor", "pointer")
        property("transition", "transform 140ms ease, background-color 140ms ease, border-color 140ms ease")

        self + hover style {
            backgroundColor(Color("rgba(255,255,255,1)"))
            property("border-color", "rgba(15,23,42,0.14)")
            property("transform", "translateY(-1px)")
        }
        self + active style { property("transform", "translateY(0)") }
    }

    // Icon-only: compact, same size everywhere
    val pointActionIconOnly by style {
        width(26.px)
        minWidth(26.px)
        height(26.px)
        padding(0.px)
        borderRadius(6.px)
        fontSize(11.px)
        fontWeight(700)
    }

    val pointActionAdd by style {
        // keeps default visuals
    }

    val pointActionConfirm by style {
        property("border-color", "rgba(34,197,94,0.26)")
        backgroundColor(Color("rgba(34,197,94,0.10)"))
        color(Color("rgba(21,128,61,0.92)"))
    }

    val pointActionCancel by style {
        property("border-color", "rgba(15,23,42,0.12)")
        backgroundColor(Color("rgba(15,23,42,0.04)"))
        color(Color("rgba(15,23,42,0.74)"))
    }

    // -------------------- Copyable row --------------------
    val copyableRow by style {
        property("cursor", "pointer")
        property("transition", "background-color 120ms ease")
        property("outline", "none")
        self + hover style { backgroundColor(Color("rgba(15,23,42,0.02)")) }
        self + focus style {
            property("outline", "none")
            property("box-shadow", "none")
        }
    }

    val copyButton by style {
        property("margin-left", "auto")
        property("padding", "4px 6px")
        property("border-radius", "8px")
        flexShrink(0)
        property("flex", "0 0 auto")
        property("min-width", "28px")
        property("height", "24px")
        property("align-self", "center")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)

        property("border", "1px solid rgba(15,23,42,0.08)")
        property("background", "rgba(255,255,255,0.85)")
        property("color", "rgba(31,42,68,0.70)")
        property("box-shadow", "0 1px 2px rgba(15,23,42,0.04)")

        property("font-size", "11px")
        property("font-weight", "600")
        property("line-height", "1")
        property("text-align", "center")

        property("transform", "translateY(0px)")
        property("transition", "opacity 120ms ease, transform 120ms ease, background-color 120ms ease, border-color 120ms ease")
        property("cursor", "pointer")

        self + hover style {
            property("background", "rgba(255,255,255,0.95)")
            property("border-color", "rgba(15,23,42,0.12)")
            property("transform", "translateY(-1px)")
            property("box-shadow", "0 2px 4px rgba(15,23,42,0.06)")
        }
    }
}
